import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import PlusOutlined from "@ant-design/icons/lib/icons/PlusOutlined";

import BaseTabs from "@/components_v2/Tabs/BaseTabs";
import OrganizationList from "@/features/organizations/list";
import UserList from "@/features/users/list";
import BaseButton from "@/components_v2/Button/BaseButton";
import BaseSvgRenderer from "@/components_v2/Svg/BaseSvgRenderer";
import { useOrganizationPermissions } from "@/features/organizations/hooks/useOrganizationPermissions";
import ImportIcon from "@/assets/icons/import.svg?react";
import CreateOrganizationModal from "@/features/organizations/list/components/CreateOrganizationModal";
import ImportOrganizationsModal from "@/features/organizations/list/components/ImportOrganizationsModal";
import { selectCurrentUser } from "@/redux/authentication/selectors";
import {
  BUTTON_SIZES,
  BUTTON_VARIANTS,
} from "@/components_v2/Button/constants";

const Directory = () => {
  const { t } = useTranslation();
  const currentUser = useSelector(selectCurrentUser);
  const { canCreate, canImport } = useOrganizationPermissions(
    currentUser,
    null,
  );

  const [openCreateOrganizationModal, setOpenCreateOrganizationModal] =
    useState(false);
  const [openImportOrganizationsModal, setOpenImportOrganizationsModal] =
    useState(false);

  const tabs = useMemo(
    () => [
      {
        key: "organizations",
        label: t("directory.labels.organizations"),
        children: <OrganizationList />,
      },
      {
        key: "individuals",
        label: t("directory.labels.individuals"),
        children: <UserList />,
      },
    ],
    [],
  );

  return (
    <div className="page-layout">
      <div className="page-header">
        <h1 className="header-l-medium">
          {t("directory.labels.signatoryMemberDirectory")}
        </h1>

        {canCreate || canImport ? (
          <div className="flex gap-200 items-center">
            {canCreate && (
              <BaseButton
                size={BUTTON_SIZES.SMALL}
                leadingIcon={<PlusOutlined />}
                onClick={() => setOpenCreateOrganizationModal(true)}
              >
                {t("organizations.labels.addNewOrganization")}
              </BaseButton>
            )}
            {canImport && (
              <BaseButton
                size={BUTTON_SIZES.SMALL}
                variant={BUTTON_VARIANTS.TERTIARY}
                leadingIcon={
                  <BaseSvgRenderer component={ImportIcon} width="20" />
                }
                onClick={() => setOpenImportOrganizationsModal(true)}
              >
                {t("organizations.labels.importOrganizations")}
              </BaseButton>
            )}
          </div>
        ) : null}
      </div>

      <p className="body-m-regular">
        {t("directory.labels.signatoryMemberDirectoryDescription")}
      </p>

      <BaseTabs items={tabs} />

      <CreateOrganizationModal
        open={openCreateOrganizationModal}
        setOpen={setOpenCreateOrganizationModal}
      />
      <ImportOrganizationsModal
        open={openImportOrganizationsModal}
        setOpen={setOpenImportOrganizationsModal}
      />
    </div>
  );
};

export default Directory;
